import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { LogOut, Trash2, CalendarDays, Inbox, CheckCircle2, Flag, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AdminCalendar } from "@/components/AdminCalendar";
import { AdminInventory } from "@/components/AdminInventory";
import { AdminSubscribers } from "@/components/AdminSubscribers";
import { AdminRoutes } from "@/components/AdminRoutes";
import { AdminLedger } from "@/components/AdminLedger";
import { AdminReports } from "@/components/AdminReports";

const STATUS_STYLES = {
    new: "bg-monster-yellow text-monster-navy",
    confirmed: "bg-monster-blue text-white",
    completed: "bg-monster-green text-monster-navy",
    cancelled: "bg-monster-red text-white",
};

export default function AdminDashboard() {
    const [bookings, setBookings] = useState(null);
    const [tab, setTab] = useState("bookings");
    const [payMethods, setPayMethods] = useState({});
    const navigate = useNavigate();

    const load = useCallback(async () => {
        try {
            const res = await api.get("/bookings");
            setBookings(res.data);
        } catch (err) {
            if (err?.response?.status === 401) {
                localStorage.removeItem("mi_admin_token");
                navigate("/admin/login");
            } else {
                toast.error(formatApiError(err));
                setBookings([]);
            }
        }
    }, [navigate]);

    useEffect(() => {
        if (!localStorage.getItem("mi_admin_token")) {
            navigate("/admin/login");
            return;
        }
        load();
    }, [load, navigate]);

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/bookings/${id}`, { status });
            toast.success(`Marked as ${status}`);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const togglePayment = async (id, field, value) => {
        try {
            await api.patch(`/bookings/${id}/payment`, { field, value, method: payMethods[id] || "cash" });
            toast.success(value ? "Payment received — added to ledger" : "Payment unmarked — removed from ledger");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/bookings/${id}`);
            toast.success("Booking deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const logout = () => {
        localStorage.removeItem("mi_admin_token");
        navigate("/admin/login");
    };

    const todayStr = format(new Date(), "yyyy-MM-dd");
    const unpaidTotal = (bookings || [])
        .filter((b) => b.status === "confirmed" && b.event_date >= todayStr && b.price)
        .reduce((sum, b) => sum + (b.deposit_paid ? 0 : b.deposit_amount ?? 50) + (b.balance_paid ? 0 : b.price - (b.deposit_amount ?? 50)), 0);

    const stats = [
        { label: "Total requests", value: bookings?.length ?? 0, icon: Inbox, bg: "bg-monster-blue" },
        { label: "New", value: bookings?.filter((b) => b.status === "new").length ?? 0, icon: Flag, bg: "bg-monster-yellow" },
        { label: "Confirmed", value: bookings?.filter((b) => b.status === "confirmed").length ?? 0, icon: CheckCircle2, bg: "bg-monster-green" },
        { label: "Completed", value: bookings?.filter((b) => b.status === "completed").length ?? 0, icon: CalendarDays, bg: "bg-white" },
        { label: "Owed to you", value: `$${unpaidTotal.toLocaleString()}`, icon: DollarSign, bg: "bg-monster-red" },
    ];

    return (
        <main data-testid="admin-dashboard" className="min-h-screen bg-monster-cream">
            <header className="border-b-2 border-monster-navy bg-white">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
                    <div className="flex items-center gap-3">
                        <img src="/logo.png" alt="Monsters Inflatables logo" className="h-11 w-11 rounded-full border-2 border-monster-navy object-cover" />
                        <div>
                            <h1 className="font-display text-xl font-semibold leading-none text-monster-navy">Booking Dashboard</h1>
                            <span className="text-xs font-bold uppercase tracking-wider text-monster-blue">Monsters Inflatables HQ</span>
                        </div>
                    </div>
                    <button
                        data-testid="admin-logout-btn"
                        onClick={logout}
                        className="inline-flex items-center gap-2 rounded-full border-2 border-monster-navy bg-white px-4 py-2 font-display text-sm font-semibold text-monster-navy shadow-sticker-sm transition-transform hover:-translate-y-0.5"
                    >
                        <LogOut className="h-4 w-4" /> Log out
                    </button>
                </div>
            </header>

            <nav className="border-b-2 border-monster-navy bg-monster-cream">
                <div className="mx-auto flex max-w-6xl flex-wrap gap-2 px-5 py-3">
                    {[
                        ["bookings", "Bookings"],
                        ["calendar", "Calendar"],
                        ["routes", "Routes"],
                        ["ledger", "Ledger"],
                        ["reports", "Reports"],
                        ["inventory", "Inventory"],
                        ["launch", "Launch List"],
                    ].map(([key, label]) => (
                        <button
                            key={key}
                            data-testid={`tab-${key}`}
                            onClick={() => setTab(key)}
                            className={`rounded-full border-2 border-monster-navy px-5 py-2 font-display text-sm font-semibold shadow-sticker-sm transition-all hover:-translate-y-0.5 ${
                                tab === key ? "bg-monster-navy text-white" : "bg-white text-monster-navy"
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </nav>

            <div className="mx-auto max-w-6xl px-5 py-10">
                {tab === "bookings" && (
                <>
                <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
                    {stats.map((s, i) => (
                        <motion.div
                            key={s.label}
                            data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.08, type: "spring", stiffness: 120, damping: 15 }}
                            className={`rounded-3xl border-2 border-monster-navy p-5 shadow-sticker-sm ${s.bg}`}
                        >
                            <s.icon className="h-6 w-6 text-monster-navy" />
                            <div className="mt-3 font-display text-3xl font-semibold text-monster-navy">{s.value}</div>
                            <div className="text-xs font-bold uppercase tracking-wider text-monster-navy/60">{s.label}</div>
                        </motion.div>
                    ))}
                </div>

                <div className="mt-10 overflow-hidden rounded-3xl border-2 border-monster-navy bg-white shadow-sticker">
                    <div className="border-b-2 border-monster-navy px-6 py-4">
                        <h2 className="font-display text-lg font-semibold text-monster-navy">Booking Requests</h2>
                    </div>
                    {bookings === null ? (
                        <p className="p-10 text-center font-semibold text-monster-navy/50">Loading bookings...</p>
                    ) : bookings.length === 0 ? (
                        <p data-testid="bookings-empty" className="p-10 text-center font-semibold text-monster-navy/50">
                            No bookings yet — share your site and watch them roll in.
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-b-2 border-monster-navy">
                                        <TableHead className="font-display font-semibold">Party date</TableHead>
                                        <TableHead className="font-display font-semibold">Customer</TableHead>
                                        <TableHead className="font-display font-semibold">Inflatable</TableHead>
                                        <TableHead className="font-display font-semibold">Address</TableHead>
                                        <TableHead className="font-display font-semibold">Status</TableHead>
                                        <TableHead className="font-display font-semibold">Payment</TableHead>
                                        <TableHead className="font-display font-semibold">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {bookings.map((b) => (
                                        <TableRow key={b.id} data-testid={`booking-row-${b.id}`} className="border-b border-monster-navy/10">
                                            <TableCell className="whitespace-nowrap font-bold">{b.event_date}</TableCell>
                                            <TableCell>
                                                <div className="font-bold">{b.name}</div>
                                                <a href={`tel:${b.phone}`} className="text-xs font-semibold text-monster-blue">{b.phone}</a>
                                                {b.email && <div className="max-w-[180px] truncate text-xs text-monster-navy/50">{b.email}</div>}
                                                {b.notes && <div className="mt-1 max-w-[200px] text-xs italic text-monster-navy/50">"{b.notes}"</div>}
                                            </TableCell>
                                            <TableCell className="font-semibold">{b.inflatable_name}</TableCell>
                                            <TableCell className="max-w-[200px] text-sm">{b.address}{b.zip ? ` (${b.zip})` : ""}</TableCell>
                                            <TableCell>
                                                <Badge data-testid={`booking-status-${b.id}`} className={`border-2 border-monster-navy font-display capitalize ${STATUS_STYLES[b.status] || ""}`}>
                                                    {b.status}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {b.price ? (
                                                    <div className="flex flex-col items-start gap-1.5">
                                                        <span className="text-xs font-bold text-monster-navy/50">
                                                            Total ${b.price}
                                                            {b.delivery_fee ? ` (incl. $${b.delivery_fee} delivery)` : ""}
                                                        </span>
                                                        <select
                                                            data-testid={`booking-method-${b.id}`}
                                                            value={payMethods[b.id] || b.deposit_method || "cash"}
                                                            onChange={(e) => setPayMethods((m) => ({ ...m, [b.id]: e.target.value }))}
                                                            className="rounded-lg border-2 border-monster-navy bg-white px-1.5 py-1 text-[11px] font-bold"
                                                            aria-label="Payment method"
                                                        >
                                                            <option value="cash">Cash</option>
                                                            <option value="card">Card</option>
                                                            <option value="venmo">Venmo</option>
                                                        </select>
                                                        <button
                                                            data-testid={`booking-deposit-toggle-${b.id}`}
                                                            onClick={() => togglePayment(b.id, "deposit_paid", !b.deposit_paid)}
                                                            className={`rounded-full border-2 border-monster-navy px-2.5 py-1 text-[11px] font-bold shadow-sticker-sm transition-transform hover:-translate-y-0.5 ${b.deposit_paid ? "bg-monster-green text-monster-navy" : "bg-monster-yellow text-monster-navy"}`}
                                                        >
                                                            {b.deposit_paid ? "✓ Deposit paid" : `Deposit $${b.deposit_amount ?? 50} due`}
                                                        </button>
                                                        <button
                                                            data-testid={`booking-balance-toggle-${b.id}`}
                                                            onClick={() => togglePayment(b.id, "balance_paid", !b.balance_paid)}
                                                            className={`rounded-full border-2 border-monster-navy px-2.5 py-1 text-[11px] font-bold shadow-sticker-sm transition-transform hover:-translate-y-0.5 ${b.balance_paid ? "bg-monster-green text-monster-navy" : "bg-monster-yellow text-monster-navy"}`}
                                                        >
                                                            {b.balance_paid ? "✓ Balance paid" : `Balance $${b.price - (b.deposit_amount ?? 50)} due`}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs font-semibold text-monster-navy/40">Confirm to set price</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <select
                                                        data-testid={`booking-status-select-${b.id}`}
                                                        value={b.status}
                                                        onChange={(e) => setStatus(b.id, e.target.value)}
                                                        className="rounded-lg border-2 border-monster-navy bg-white px-2 py-1.5 text-xs font-bold"
                                                    >
                                                        {Object.keys(STATUS_STYLES).map((s) => (
                                                            <option key={s} value={s}>{s}</option>
                                                        ))}
                                                    </select>
                                                    <button
                                                        data-testid={`booking-delete-${b.id}`}
                                                        onClick={() => remove(b.id)}
                                                        className="rounded-lg border-2 border-monster-navy bg-white p-1.5 text-monster-red transition-colors hover:bg-red-50"
                                                        aria-label="Delete booking"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>
                </>
                )}
                {tab === "calendar" && <AdminCalendar bookings={bookings || []} />}
                {tab === "routes" && <AdminRoutes />}
                {tab === "ledger" && <AdminLedger />}
                {tab === "reports" && <AdminReports />}
                {tab === "inventory" && <AdminInventory />}
                {tab === "launch" && <AdminSubscribers />}
            </div>
        </main>
    );
}
